// pages/Visits.jsx - Complete with pagination and optimizations
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  Plus,
  Search,
  Calendar,
  User,
  Building2,
  Clock,
  Filter,
  X,
  LayoutGrid,
  List,
  ChevronDown,
  Wrench,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import apiService from "../../services/api";
import CreateVisitModal from "../../components/CreateVisitModal";
import { useTheme } from "../../contexts/ThemeContext";

const Visits = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [visits, setVisits] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  const [filters, setFilters] = useState({
    visitType: "",
    status: "",
    accountId: "",
    userId: "",
    dateFrom: "",
    dateTo: "",
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    fetchVisits();
  }, [filters, pagination.page]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [visitsRes, accountsRes, usersRes] = await Promise.all([
        apiService.getVisits({
          ...filters,
          page: pagination.page,
          limit: pagination.limit,
        }),
        apiService.getAccounts(),
        apiService.getCompanyUsers(),
      ]);

      setVisits(visitsRes.data || []);
      setAccounts(accountsRes.data || []);
      setUsers(usersRes.data || []);

      if (visitsRes.pagination) {
        setPagination((prev) => ({
          ...prev,
          total: visitsRes.pagination.total,
          totalPages: visitsRes.pagination.totalPages,
        }));
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVisits = async () => {
    try {
      setLoading(true);
      const response = await apiService.getVisits({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });

      setVisits(response.data || []);

      if (response.pagination) {
        setPagination((prev) => ({
          ...prev,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
        }));
      }
    } catch (error) {
      console.error("Error fetching visits:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVisitClick = (visit) => {
    navigate(`/visits/${visit.id}`);
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    setSelectedAccount(null);
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchVisits();
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    const clearedFilters = {
      visitType: "",
      status: "",
      accountId: "",
      userId: "",
      dateFrom: "",
      dateTo: "",
    };
    setFilters(clearedFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      completed: "bg-green-500/20 text-green-400 border-green-500/30",
      cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
      no_show: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    };
    return colors[status] || colors.scheduled;
  };

  const getVisitTypeIcon = (visitType) => {
    if (visitType === "technical") {
      return <Wrench className="w-4 h-4" />;
    }
    return <TrendingUp className="w-4 h-4" />;
  };

  const getVisitTypeColor = (visitType) => {
    const colors = {
      technical: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      sales: "bg-green-500/20 text-green-400 border-green-500/30",
    };
    return colors[visitType] || colors.technical;
  };

  const formatDate = (date) => {
    if (!date) return "Not set";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "Not set";
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const filteredVisits = visits.filter((visit) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      visit.account?.name?.toLowerCase().includes(searchLower) ||
      visit.visitName?.toLowerCase().includes(searchLower) ||
      visit.jobDetails?.toLowerCase().includes(searchLower) ||
      visit.vsistNotes?.toLowerCase().includes(searchLower)
    );
  });

  const activeFiltersCount = Object.values(filters).filter(
    (v) => v !== "",
  ).length;

  const renderPaginationNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`relative min-w-[44px] px-4 py-2.5 rounded-lg font-semibold transition-all duration-200 ${
            pagination.page === i
              ? "bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30 scale-105 border border-blue-500/50"
              : isDarkMode
                ? "bg-[#1A1D24] border border-gray-700 text-gray-300 hover:border-blue-500/50 hover:bg-blue-500/10 hover:text-blue-400 hover:scale-105"
                : "bg-white border border-gray-300 text-gray-600 hover:border-blue-500/50 hover:bg-blue-500/10 hover:text-blue-600 hover:scale-105"
          }`}
        >
          {i}
        </button>,
      );
    }

    return pages;
  };

  // Shared classes for pagination nav buttons
  const paginationNavBtn = isDarkMode
    ? "bg-[#1A1D24] border-gray-700 text-gray-400 disabled:hover:border-gray-700 disabled:hover:bg-[#1A1D24] disabled:hover:text-gray-400"
    : "bg-white border-gray-300 text-gray-500 disabled:hover:border-gray-300 disabled:hover:bg-white disabled:hover:text-gray-500";

  const inputClass = isDarkMode
    ? "bg-[#13161B] border-gray-700 text-white placeholder-gray-500 focus:ring-blue-500 focus:border-transparent"
    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-transparent";

  const selectClass = isDarkMode
    ? "bg-[#0C0E12] border-gray-700 text-white focus:ring-blue-500"
    : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500";

  return (
    <div
      className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
    >
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
              }}
            >
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <h1
              className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Visits
            </h1>
          </div>
          <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            Schedule and track sales and technical visits
          </p>
        </div>

        {/* Stats */}
        {!loading && pagination.total > 0 && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div
              className={`border rounded-lg p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Total Visits
              </div>
              <div
                className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {pagination.total}
              </div>
            </div>
            <div
              className={`border rounded-lg p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Scheduled
              </div>
              <div className="text-2xl font-bold text-blue-400">
                {visits.filter((v) => v.status === "scheduled").length}
              </div>
            </div>
            <div
              className={`border rounded-lg p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Completed
              </div>
              <div className="text-2xl font-bold text-green-400">
                {visits.filter((v) => v.status === "completed").length}
              </div>
            </div>
            <div
              className={`border rounded-lg p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Current Page
              </div>
              <div
                className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {pagination.page} / {pagination.totalPages}
              </div>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search visits by account, visit name, or details..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 ${inputClass}`}
              />
            </div>

            <div className="flex gap-3">
              {/* View Toggle */}
              <div
                className={`flex border rounded-lg overflow-hidden ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-300"}`}
              >
                <button
                  onClick={() => setViewMode("list")}
                  className={`px-3 py-2 ${
                    viewMode === "list"
                      ? "bg-blue-600 text-white"
                      : isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("grid")}
                  className={`px-3 py-2 border-l ${isDarkMode ? "border-gray-700" : "border-gray-300"} ${
                    viewMode === "grid"
                      ? "bg-blue-600 text-white"
                      : isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>

              {/* Filters Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2 border rounded-lg transition-colors flex items-center gap-2 ${isDarkMode ? "bg-[#13161B] border-gray-700 text-white hover:bg-gray-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"}`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {activeFiltersCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 rounded-lg text-white font-medium flex items-center gap-2 transition-all"
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                }}
              >
                <Plus className="w-4 h-4" />
                New Visit
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div
              className={`border rounded-lg p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3
                  className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Filters
                </h3>
                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Clear All
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {/* Visit Type */}
                <div>
                  <label
                    className={`block text-sm mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Visit Type
                  </label>
                  <select
                    value={filters.visitType}
                    onChange={(e) =>
                      handleFilterChange({
                        ...filters,
                        visitType: e.target.value,
                      })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 ${selectClass}`}
                  >
                    <option value="">All Types</option>
                    <option value="sales">Sales</option>
                    <option value="technical">Technical</option>
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label
                    className={`block text-sm mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) =>
                      handleFilterChange({ ...filters, status: e.target.value })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 ${selectClass}`}
                  >
                    <option value="">All Statuses</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="no_show">No Show</option>
                  </select>
                </div>

                {/* Account */}
                <div>
                  <label
                    className={`block text-sm mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Account
                  </label>
                  <select
                    value={filters.accountId}
                    onChange={(e) =>
                      handleFilterChange({
                        ...filters,
                        accountId: e.target.value,
                      })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 ${selectClass}`}
                  >
                    <option value="">All Accounts</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date From */}
                <div>
                  <label
                    className={`block text-sm mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Date From
                  </label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) =>
                      handleFilterChange({
                        ...filters,
                        dateFrom: e.target.value,
                      })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 ${selectClass}`}
                  />
                </div>

                {/* Date To */}
                <div>
                  <label
                    className={`block text-sm mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Date To
                  </label>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) =>
                      handleFilterChange({ ...filters, dateTo: e.target.value })
                    }
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 ${selectClass}`}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Visits Display */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                Loading visits...
              </div>
            </div>
          </div>
        ) : filteredVisits.length === 0 ? (
          <div className="text-center py-20">
            <MapPin className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3
              className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              No visits found
            </h3>
            <p
              className={`mb-4 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              {activeFiltersCount > 0 || searchTerm
                ? "Try adjusting your filters or search term"
                : "Schedule your first visit to get started"}
            </p>
            {activeFiltersCount === 0 && !searchTerm && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-2 rounded-lg text-white font-medium"
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                }}
              >
                Schedule Visit
              </button>
            )}
          </div>
        ) : viewMode === "list" ? (
          /* List View */
          <div
            className={`border rounded-lg overflow-hidden ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}>
                  <tr>
                    {[
                      "Visit Name",
                      "Account/Opportunity",
                      "Type",
                      "Date & Time",
                      "Assigned To",
                      "Status",
                    ].map((h) => (
                      <th
                        key={h}
                        className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody
                  className={`divide-y ${isDarkMode ? "divide-gray-700" : "divide-gray-200"}`}
                >
                  {filteredVisits.map((visit) => (
                    <tr
                      key={visit.id}
                      onClick={() => handleVisitClick(visit)}
                      className={`cursor-pointer transition-colors ${isDarkMode ? "hover:bg-[#0C0E12]" : "hover:bg-gray-50"}`}
                    >
                      <td className="px-6 py-4">
                        <div className="text-blue-400 font-medium hover:text-blue-300">
                          {visit.visitName}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {visit.account?.name ?? visit.opportunity?.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 w-fit ${getVisitTypeColor(visit.visitType)}`}
                        >
                          {getVisitTypeIcon(visit.visitType)}
                          {visit.visitType === "technical"
                            ? "Technical"
                            : "Sales"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className={`flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                        >
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {formatDateTime(visit.visitDateTime)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className={`flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                        >
                          <User className="w-4 h-4 text-gray-400" />
                          {visit.visitType === "technical"
                            ? visit.engineer?.name
                            : visit.salesMember?.name || "Unassigned"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(visit.status)}`}
                        >
                          {visit.status.replace("_", " ").toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVisits.map((visit) => (
              <div
                key={visit.id}
                onClick={() => handleVisitClick(visit)}
                className={`border rounded-lg p-6 hover:border-blue-500 cursor-pointer transition-all ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3
                      className={`font-semibold mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {visit.visitName}
                    </h3>
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <Building2 className="w-4 h-4" />
                      {visit.account?.name}
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getVisitTypeColor(visit.visitType)}`}
                  >
                    {getVisitTypeIcon(visit.visitType)}
                    {visit.visitType === "technical" ? "Tech" : "Sales"}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div
                    className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                  >
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {formatDateTime(visit.visitDateTime)}
                  </div>
                  <div
                    className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                  >
                    <User className="w-4 h-4 text-gray-400" />
                    {visit.visitType === "technical"
                      ? visit.engineer?.name
                      : visit.salesMember?.name || "Unassigned"}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(visit.status)}`}
                  >
                    {visit.status.replace("_", " ").toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && filteredVisits.length > 0 && pagination.totalPages > 1 && (
          <div
            className={`mt-8 flex flex-col sm:flex-row items-center justify-between gap-6 border rounded-xl p-6 shadow-lg ${isDarkMode ? "bg-gradient-to-br from-[#13161B] to-[#0C0E12] border-gray-700/50" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-center gap-3">
              <div className="hidden sm:block w-1 h-10 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full" />
              <div>
                <div
                  className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                >
                  Showing{" "}
                  <span className="text-blue-400 font-semibold">
                    {Math.min(
                      (pagination.page - 1) * pagination.limit + 1,
                      pagination.total,
                    )}
                  </span>{" "}
                  to{" "}
                  <span className="text-blue-400 font-semibold">
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total,
                    )}
                  </span>
                </div>
                <div
                  className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  of {pagination.total} total visits
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* First Page */}
              <button
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
                className={`group relative px-3 py-2.5 border rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:border-blue-500/50 hover:bg-blue-500/10 hover:text-blue-400 transition-all duration-200 ${paginationNavBtn}`}
                title="First page"
              >
                <div className="flex items-center">
                  <ChevronLeft className="w-4 h-4" />
                  <ChevronLeft className="w-4 h-4 -ml-3" />
                </div>
              </button>

              {/* Previous Page */}
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className={`group relative px-4 py-2.5 border rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:border-blue-500/50 hover:bg-blue-500/10 hover:text-blue-400 transition-all duration-200 font-medium ${paginationNavBtn}`}
                title="Previous page"
              >
                <div className="flex items-center gap-1.5">
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Prev</span>
                </div>
              </button>

              {/* Page Numbers */}
              <div className="hidden sm:flex items-center gap-1.5">
                {renderPaginationNumbers()}
              </div>

              {/* Current Page (Mobile) */}
              <div className="sm:hidden px-5 py-2.5 bg-gradient-to-br from-blue-600 to-blue-700 border border-blue-500/50 rounded-lg text-white font-semibold shadow-lg shadow-blue-500/20">
                <div className="flex items-center gap-2">
                  <span className="text-sm">Page</span>
                  <span className="text-base">{pagination.page}</span>
                  <span className="text-xs text-blue-200">
                    / {pagination.totalPages}
                  </span>
                </div>
              </div>

              {/* Next Page */}
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className={`group relative px-4 py-2.5 border rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:border-blue-500/50 hover:bg-blue-500/10 hover:text-blue-400 transition-all duration-200 font-medium ${paginationNavBtn}`}
                title="Next page"
              >
                <div className="flex items-center gap-1.5">
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </button>

              {/* Last Page */}
              <button
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.page >= pagination.totalPages}
                className={`group relative px-3 py-2.5 border rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:border-blue-500/50 hover:bg-blue-500/10 hover:text-blue-400 transition-all duration-200 ${paginationNavBtn}`}
                title="Last page"
              >
                <div className="flex items-center">
                  <ChevronRight className="w-4 h-4" />
                  <ChevronRight className="w-4 h-4 -ml-3" />
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Visit Modal */}
      {showCreateModal && (
        <CreateVisitModal
          account={selectedAccount}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedAccount(null);
          }}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
};

export default Visits;
